import { useContext, useEffect } from "react";
import { Outlet } from "react-router-dom";
import { useMsal } from "@azure/msal-react";

import { AppStateContext } from "@/state/AppStateContext";
import { useUnifiedAuth } from "@/auth/useUnifiedAuth";
import { createLoginRequest } from "@/auth/authConfig";
import { redirectOnce } from "@/auth/authRedirect";
import { authSettings } from "@/auth/authInstances";
import { getUserPhoto, getUserPhotoWithToken } from "@/auth/graphService";
import { getTeamsAuthContext } from "@/auth/teamsAuth";
import Joyride from "@/components/Joyride/Joyride";
import Sidebar from "@/components/Sidebar/Sidebar";
import CitationsPanel from "@/components/Citations/CitationsPanel";
import { useVisualViewportLock } from "@/hooks/useVisualViewportLock";
import logo from "@/assets/logo.png";

import "./Root.css";

const Home = () => {
  const appStateContext = useContext(AppStateContext);
  const AUTH_VERSION = "2.0.1";
  const { instance } = useMsal();
  const { isAuthenticated, account, inProgress, isInTeams } = useUnifiedAuth();
  useVisualViewportLock();

  useEffect(() => {
    const currentVersion = localStorage.getItem('sms-gpt_authVersion');
    if (currentVersion !== AUTH_VERSION) {
      // Clear persisted MSAL auth state so login is re-triggered after auth changes.
      for (const storage of [localStorage, sessionStorage]) {
        const msalKeys = Object.keys(storage).filter((key) => key.startsWith('msal.'));
        for (const key of msalKeys) storage.removeItem(key);
      }
      localStorage.setItem("sms-gpt_authVersion", AUTH_VERSION);
      window.location.reload();
    }
  }, []);

  useEffect(() => {
    // If not authenticated and no interaction in progress, trigger login (skip in Teams)
    if (!isAuthenticated && inProgress === "none" && !isInTeams) {
      const loginRequest = createLoginRequest(authSettings);
      redirectOnce(() => instance.loginRedirect(loginRequest)).catch((error) => console.error("Login redirect error:", error));
    }
    // Otherwise, set user info in global state
    else if (isAuthenticated && account) {
      // Fetch user photo asynchronously
      const fetchUserPhoto = async () => {
        let photoUrl: string | null = null;
        
        if (isInTeams) {
          // For Teams, use the token from Teams context
          const teamsContext = getTeamsAuthContext();
          if (teamsContext.token) photoUrl = await getUserPhotoWithToken(teamsContext.token);
        } 
        // For MSAL, use the instance to get photo
        else photoUrl = await getUserPhoto(instance);
        
        // Set user info with photo
        appStateContext?.dispatch({ 
          type: 'SET_CURRENT_USER', 
          payload: { 
            id: account.localAccountId, 
            name: account.name || account.username,
            email: account.username || '',
            photo: photoUrl || undefined
          }
        });
      };
      
      // Set user info immediately (without photo)
      appStateContext?.dispatch({ 
        type: 'SET_CURRENT_USER', 
        payload: { 
          id: account.localAccountId, 
          name: account.name || account.username,
          email: account.username || '',
        }
      });
      
      // Then fetch and update with photo
      fetchUserPhoto();
      
      // Get groups from token claims
      const idTokenClaims = account.idTokenClaims as any;
      if (idTokenClaims?.groups) appStateContext?.dispatch({ type: 'SET_USER_GROUPS', payload: idTokenClaims.groups });
    }
  }, [isAuthenticated, account, inProgress, instance, isInTeams]);
  
  /**
   * Update document title when in DEV/TST environments
  **/
  useEffect(() => { if (appStateContext?.state.frontendSettings?.display_env == 'DEV' || appStateContext?.state.frontendSettings?.display_env == 'TST') document.title = "ALLY Chat | SMS group - " + appStateContext.state.frontendSettings.display_env }, [appStateContext?.state.frontendSettings]);
  
  /**
   * Handle manually triggering auth
  **/
  const triggerAuth = async () => {
    // Teams auth happens automatically, so just log a message
    if (isInTeams) {
      console.log('In Teams environment - authentication handled by Teams SDK');
      return;
    }
    
    // If not authenticated and no interaction in progress, trigger login
    if (!isAuthenticated && inProgress === "none") {
      const loginRequest = createLoginRequest(authSettings);
      redirectOnce(() => instance.loginRedirect(loginRequest)).catch((error) => console.error("Login redirect error:", error));
    }
    // Otherwise, set user info in global state
    else if (isAuthenticated && account) {
      // Fetch user photo asynchronously
      let photoUrl: string | null = null;
      
      if (isInTeams) {
        const teamsContext = getTeamsAuthContext();
        if (teamsContext.token) photoUrl = await getUserPhotoWithToken(teamsContext.token);
      }
      else photoUrl = await getUserPhoto(instance);
      
      // Set user info with photo
      appStateContext?.dispatch({ 
        type: 'SET_CURRENT_USER', 
        payload: { 
          id: account.localAccountId, 
          name: account.name || account.username,
          email: account.username || '',
          photo: photoUrl || undefined
        }
      });
      
      // Get groups from token claims
      const idTokenClaims = account.idTokenClaims as any;
      if (idTokenClaims?.groups) appStateContext?.dispatch({ type: 'SET_USER_GROUPS', payload: idTokenClaims.groups });
    }
  }

  // Show nothing while MSAL is initializing to prevent flash
  if (inProgress !== "none") return null;

  const displayEnv = appStateContext?.state.frontendSettings?.display_env;
  const isNonProductionEnv = displayEnv === "DEV" || displayEnv === "TST";

  return (
    (isAuthenticated ?
      <div className="root-container bg-neutral">
        <Joyride />

        {isNonProductionEnv && (
          <div className="environment-notice" role="status">
            <span className="environment-notice__indicator" aria-hidden="true" />
            <span className="environment-notice__code">{displayEnv}</span>
          </div>
        )}

        <Sidebar />

        <Outlet />

        <CitationsPanel />
      </div>
    :
      <div className="root-container flex items-center justify-center bg-neutral">
        <div className="flex flex-col items-center bg-base-100 rounded-3xl p-16 shadow-lg -mt-24">
          <div className="flex items-center">
            <img src={logo} alt="ALLY" className="h-[150px] w-auto" />
          </div>
          <button type="button" className="btn btn-lg btn-primary mt-12" onClick={triggerAuth}>Login</button>
        </div>
      </div>
    )
  );
}

export default Home;
